import { computed } from 'vue'

/**
 * Composable for calculating distance between two geographical coordinates
 * Uses the Haversine formula to calculate the great-circle distance
 */
export function useDistanceCalculation() {
  // Sample company location (Amsterdam)
  const companyLocation = {
    lat: 51.86122591918386,
    lng: 4.990735425155165
  }

  /**
   * Calculate distance between two points using Haversine formula
   * @param {Object} point1 - First coordinate point {lat, lng}
   * @param {Object} point2 - Second coordinate point {lat, lng}
   * @returns {number} Distance in kilometers
   */
  function calculateDistance(point1, point2) {
    if (!point1 || !point2 || !point1.lat || !point1.lng || !point2.lat || !point2.lng) {
      return 0
    }

    const R = 6371 // Earth's radius in kilometers
    const dLat = toRadians(point2.lat - point1.lat)
    const dLng = toRadians(point2.lng - point1.lng)
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(toRadians(point1.lat)) * Math.cos(toRadians(point2.lat)) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2)
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const distance = R * c
    
    return Math.round(distance * 100) / 100 // Round to 2 decimal places
  }

  /**
   * Convert degrees to radians
   * @param {number} degrees
   * @returns {number} radians
   */
  function toRadians(degrees) {
    return degrees * (Math.PI / 180)
  }

  /**
   * Calculate distance from user location to company building
   * @param {Object} userLocation - User's coordinates {lat, lng}
   * @returns {number} Distance in kilometers
   */
  function getDistanceToCompany(userLocation) {
    return calculateDistance(userLocation, companyLocation)
  }

  /**
   * Create a computed property for distance calculation
   * @param {Ref} userLocationRef - Reactive reference to user location
   * @returns {ComputedRef<number>} Computed distance in kilometers
   */
  function useDistanceComputed(userLocationRef) {
    return computed(() => {
      if (!userLocationRef.value) return 0
      return getDistanceToCompany(userLocationRef.value)
    })
  }

  return {
    companyLocation,
    calculateDistance,
    getDistanceToCompany,
    useDistanceComputed
  }
}